"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type ComponentProps } from "react";
import { Icon } from "@/components/icons/interface";
import { CapabilityWorkbench, packageNames, type PackageName } from "@/components/home/capability-workbench";
import { ProjectGraph } from "@/components/home/project-graph";
import { ProjectSettings } from "@/components/home/project-settings";
type Topic = {
  name: string;
  icon: ComponentProps<typeof Icon>["name"];
  title: string;
  description: string;
  color?: string;
};
type TopicId = "project" | "folder" | "graph" | "config" | "core" | "files" | "gpu";
const topics: Record<TopicId, Topic> = {
  project: {
    name: "Playground",
    icon: "layers",
    title: "Playground",
    description: "Project"
  },
  folder: {
    name: "Functions",
    icon: "folder",
    title: "Functions",
    description: "Folder"
  },
  graph: {
    name: "Double",
    icon: "code",
    title: "Double",
    description: "Type",
    color: "violet"
  },
  config: {
    name: "Settings",
    icon: "settings",
    title: "Settings",
    description: "Project settings"
  },
  core: {
    name: "Core",
    icon: "package",
    title: "Core",
    description: "Library"
  },
  files: {
    name: "Files",
    icon: "package",
    title: "Files",
    description: "Package"
  },
  gpu: {
    name: "GPU",
    icon: "package",
    title: "GPU",
    description: "Package"
  }
};
const activities = {
  project: {
    name: "Project",
    icon: "layers"
  },
  packages: {
    name: "Packages",
    icon: "package"
  }
} as const;
type Activity = keyof typeof activities;
const instances = [{
  id: "project",
  level: 1,
  parent: null
}, {
  id: "folder",
  level: 2,
  parent: "project"
}, {
  id: "graph",
  level: 3,
  parent: "folder"
}] as const;
type InstanceId = typeof instances[number]["id"];
function TopicIcon(t0) {
  const $ = _c(5);
  const {
    id
  } = t0;
  const t1 = `anatomy-icon anatomy-${(topics[id] as Topic).color || "neutral"}`;
  const t2 = topics[id];
  let t3;
  if ($[0] !== t2.icon) {
    t3 = <Icon name={t2.icon} />;
    $[0] = t2.icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t3) {
    t4 = <span aria-hidden="true" className={t1}>{t3}</span>;
    $[2] = t1;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function FolderView(t0) {
  const $ = _c(6);
  const {
    id,
    open
  } = t0;
  let t1;
  if ($[0] !== id) {
    t1 = id === "project" ? [{
      id: "folder",
      type: "Folder"
    }] : [{
      id: "graph",
      type: "Type"
    }];
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const children = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="anatomy-folder-caption"><span>Name</span><span>Type</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== open) {
    t3 = <div className="anatomy-folder-view">{t2}{children.map(child => <button key={child.id} aria-label={`${topics[child.id].name}, ${child.type}`} onClick={() => open(child.id, true)}><TopicIcon id={child.id} /><strong>{topics[child.id].name}</strong><span>{child.type}</span></button>)}</div>;
    $[3] = children;
    $[4] = open;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function ProjectExplorer() {
  const $ = _c(56);
  const [active, setActive] = useState("graph");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["graph"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [tabs, setTabs] = useState(t0);
  const [activity, setActivity] = useState("project");
  const [mobileOpen, setMobileOpen] = useState(false);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["project", "folder"];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [expanded, setExpanded] = useState(t1);
  const [treeFocus, setTreeFocus] = useState("project");
  const [selectedInstance, setSelectedInstance] = useState("graph");
  const tabList = useRef(null);
  const focusTab = useRef(false);
  const mobileToggle = useRef(null);
  const sidebar = useRef(null);
  let t2;
  if ($[2] !== expanded) {
    t2 = item => item.id === "project" || expanded.includes("project") && (item.id !== "graph" || expanded.includes("folder"));
    $[2] = expanded;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const visible = instances.filter(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function selectActivity(id) {
      setActivity(id);
      if (window.matchMedia("(max-width: 760px)").matches) {
        setMobileOpen(true);
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const selectActivity = t3;
  const open = function open(id_0, t4) {
    const focus = t4 === undefined ? false : t4;
    focusTab.current = focus || window.matchMedia("(max-width: 760px)").matches;
    setTabs(current => current.includes(id_0) ? current : [...current, id_0]);
    setActive(id_0);
    setMobileOpen(false);
    if (instances.some(item_0 => item_0.id === id_0)) {
      setSelectedInstance(id_0 as InstanceId);
    }
  };
  let t5;
  let t6;
  if ($[5] !== activity || $[6] !== mobileOpen) {
    t5 = () => {
      if (mobileOpen) {
        sidebar.current?.querySelector(activity === "project" ? "[role=\"treeitem\"][tabindex=\"0\"]" : "button")?.focus({
          preventScroll: true
        });
      }
    };
    t6 = [mobileOpen, activity];
    $[5] = activity;
    $[6] = mobileOpen;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const close = function close(id_1) {
    const remaining = tabs.filter(tab => tab !== id_1);
    focusTab.current = true;
    setTabs(remaining);
    if (active === id_1) {
      const next = remaining[Math.max(0, tabs.indexOf(id_1) - 1)];
      setActive(next);
      if (instances.some(item_1 => item_1.id === next)) {
        setSelectedInstance(next as InstanceId);
      }
    }
  };
  let t7;
  if ($[9] !== active || $[10] !== mobileOpen || $[11] !== tabs) {
    t7 = [active, tabs, mobileOpen];
    $[9] = active;
    $[10] = mobileOpen;
    $[11] = tabs;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useEffect(() => {
    const tab_0 = tabList.current?.querySelector("[aria-selected=\"true\"]");
    if (!tab_0 || !tabList.current) {
      return;
    }
    const parent = tab_0.parentElement;
    if (parent.offsetLeft < tabList.current.scrollLeft) {
      tabList.current.scrollLeft = parent.offsetLeft;
    } else {
      if (parent.offsetLeft + parent.offsetWidth > tabList.current.scrollLeft + tabList.current.clientWidth) {
        tabList.current.scrollLeft = parent.offsetLeft + parent.offsetWidth - tabList.current.clientWidth;
      }
    }
    if (focusTab.current) {
      tab_0.focus({
        preventScroll: true
      });
      focusTab.current = false;
    }
  }, t7);
  const focusInstance = function focusInstance(id_2) {
    setTreeFocus(id_2);
    sidebar.current?.querySelector(`[data-instance="${id_2}"]`)?.focus({
      preventScroll: true
    });
  };
  const toggle = function toggle(id_3) {
    setExpanded(current_0 => current_0.includes(id_3) ? current_0.filter(key => key !== id_3) : [...current_0, id_3]);
    focusInstance(id_3);
  };
  const sourceIds = {
    Core: "core",
    Files: "files",
    GPU: "gpu"
  };
  const openSource = function openSource(source) {
    setActivity("packages");
    open(sourceIds[source], true);
  };
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span id="architecture" className="atlas-section-anchor" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <header className="anatomy-introduction"><div><p className="atlas-eyebrow" data-reveal={true}>The workspace</p><h2 data-reveal="1">What will you build?</h2></div></header>;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== mobileOpen) {
    t10 = event => {
      if (event.key === "Escape" && mobileOpen) {
        setMobileOpen(false);
        mobileToggle.current?.focus({
          preventScroll: true
        });
      }
    };
    $[15] = mobileOpen;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = Object.keys(activities);
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== activity) {
    t12 = (t11 as Activity[]).map(id_4 => <button key={id_4} className="anatomy-activity" aria-label={activities[id_4].name} aria-pressed={activity === id_4} aria-controls="anatomy-sidebar-panel" onClick={() => selectActivity(id_4)}><Icon name={activities[id_4].icon} /><span aria-hidden="true">{activities[id_4].name}</span></button>);
    $[18] = activity;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Icon name="settings" />;
    t14 = <span aria-hidden="true">Settings</span>;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  const t15 = `${activities[activity].name} browser`;
  const t16 = activities[activity];
  let t17;
  if ($[22] !== t16.name) {
    t17 = <div className="anatomy-explorer-heading"><span>{t16.name}</span></div>;
    $[22] = t16.name;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  const t18 = activity === "project" ? <div role="tree" aria-label="Project instances">{visible.map((instance, index) => {
      const branch = instance.id === "project" || instance.id === "folder";
      return <div key={instance.id} role="treeitem" data-instance={instance.id} aria-label={topics[instance.id].name} aria-level={instance.level} aria-posinset={1} aria-setsize={1} aria-expanded={branch ? expanded.includes(instance.id) : undefined} aria-selected={selectedInstance === instance.id} tabIndex={treeFocus === instance.id ? 0 : -1} className="anatomy-tree-item" style={{
        paddingLeft: 6 + (instance.level - 1) * 16
      }} onFocus={() => setTreeFocus(instance.id)} onClick={() => open(instance.id)} onKeyDown={event_0 => {
        if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home", "End", "Enter", " "].includes(event_0.key)) {
          event_0.preventDefault();
        }
        if (event_0.key === "ArrowDown" || event_0.key === "ArrowUp") {
          focusInstance(visible[Math.max(0, Math.min(visible.length - 1, index + (event_0.key === "ArrowDown" ? 1 : -1)))].id);
        }
        if (event_0.key === "Home" || event_0.key === "End") {
          focusInstance(visible[event_0.key === "Home" ? 0 : visible.length - 1].id);
        }
        if (event_0.key === "ArrowRight" && branch) {
          if (!expanded.includes(instance.id)) {
            toggle(instance.id);
          } else {
            if (visible[index + 1]) {
              focusInstance(visible[index + 1].id);
            }
          }
        }
        if (event_0.key === "ArrowLeft") {
          if (branch && expanded.includes(instance.id)) {
            toggle(instance.id);
          } else {
            if (instance.parent) {
              focusInstance(instance.parent);
            }
          }
        }
        if (event_0.key === "Enter" || event_0.key === " ") {
          open(instance.id);
        }
      }}><span className="anatomy-tree-chevron" aria-hidden="true" onClick={event_1 => {
          if (branch) {
            event_1.stopPropagation();
            toggle(instance.id);
          }
        }}>{branch && <Icon name="chevron" />}</span><TopicIcon id={instance.id} /><span>{topics[instance.id].name}</span><small>{topics[instance.id].description}</small></div>;
    })}</div> : <div className="anatomy-package-list">{packageNames.map(name => <button key={name} className="anatomy-package-list-item" aria-pressed={active === sourceIds[name]} onClick={() => open(sourceIds[name])}><Icon name="package" /><span><strong>{name}</strong></span></button>)}</div>;
  let t19;
  if ($[24] !== mobileOpen || $[25] !== t15 || $[26] !== t17 || $[27] !== t18) {
    t19 = <aside ref={sidebar} id="anatomy-sidebar-panel" className="anatomy-explorer" aria-label={t15} data-open={mobileOpen}>{t17}{t18}</aside>;
    $[24] = mobileOpen;
    $[25] = t15;
    $[26] = t17;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = "anatomy-main";
  const t21 = "anatomy-tabbar";
  const t22 = mobileOpen ? "Close workspace browser" : "Open workspace browser";
  let t23;
  if ($[29] !== mobileOpen) {
    t23 = () => setMobileOpen(!mobileOpen);
    $[29] = mobileOpen;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const t24 = mobileOpen ? "close" : "menu";
  let t25;
  if ($[31] !== t24) {
    t25 = <Icon name={t24} />;
    $[31] = t24;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] !== mobileOpen || $[34] !== t22 || $[35] !== t23 || $[36] !== t25) {
    t26 = <button ref={mobileToggle} aria-controls="anatomy-sidebar-panel" className="anatomy-mobile-toggle" aria-label={t22} aria-expanded={mobileOpen} onClick={t23}>{t25}</button>;
    $[33] = mobileOpen;
    $[34] = t22;
    $[35] = t23;
    $[36] = t25;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  const t27 = "anatomy-tabs";
  const t28 = "tablist";
  const t29 = "Open editors";
  const t30 = tabs.map(id_5 => <div key={id_5} role="presentation" data-active={active === id_5}><button id={`anatomy-tab-${id_5}`} role="tab" aria-label={topics[id_5].name} aria-selected={active === id_5} aria-controls={`anatomy-pane-${id_5}`} tabIndex={active === id_5 ? 0 : -1} onClick={() => open(id_5)} onKeyDown={event_2 => {
      const index_0 = tabs.indexOf(id_5);
      const next_0 = event_2.key === "ArrowRight" ? (index_0 + 1) % tabs.length : event_2.key === "ArrowLeft" ? (index_0 - 1 + tabs.length) % tabs.length : event_2.key === "Home" ? 0 : event_2.key === "End" ? tabs.length - 1 : null;
      if (next_0 !== null) {
        event_2.preventDefault();
        open(tabs[next_0], true);
      }
      if (event_2.key === "Delete" && id_5 !== "graph") {
        event_2.preventDefault();
        close(id_5);
      }
    }}><TopicIcon id={id_5} />{topics[id_5].name}</button>{id_5 !== "graph" && <button className="anatomy-close-tab" tabIndex={active === id_5 ? 0 : -1} aria-label={`Close ${topics[id_5].name} tab`} onClick={() => close(id_5)}><Icon name="close" /></button>}</div>);
  let t31;
  if ($[38] !== t30) {
    t31 = <div className={t27} ref={tabList} role={t28} aria-label={t29}>{t30}</div>;
    $[38] = t30;
    $[39] = t31;
  } else {
    t31 = $[39];
  }
  let t32;
  if ($[40] !== t26 || $[41] !== t31) {
    t32 = <div className={t21}>{t26}{t31}</div>;
    $[40] = t26;
    $[41] = t31;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  let t33;
  if ($[43] !== active || $[44] !== open || $[45] !== openSource || $[46] !== tabs) {
    let t34;
    if ($[48] !== active || $[49] !== open || $[50] !== openSource) {
      t34 = id_6 => <div key={id_6} className="anatomy-pane" role="tabpanel" id={`anatomy-pane-${id_6}`} aria-labelledby={`anatomy-tab-${id_6}`} hidden={active !== id_6} tabIndex={0}>{!["graph", "files", "gpu", "core", "config"].includes(id_6) && <div className="anatomy-pane-copy"><h3>{topics[id_6].title}</h3><p>{topics[id_6].description}</p></div>}{id_6 === "graph" ? <ProjectGraph openCore={() => openSource("Core")} /> : id_6 === "config" ? <ProjectSettings /> : id_6 === "project" || id_6 === "folder" ? <div className="anatomy-stage anatomy-stage-folder"><FolderView id={id_6} open={open} /></div> : <CapabilityWorkbench source={topics[id_6].name as PackageName} initial={id_6 === "core" ? "multiply" : id_6 === "files" ? "open" : "renderer"} />}</div>;
      $[48] = active;
      $[49] = open;
      $[50] = openSource;
      $[51] = t34;
    } else {
      t34 = $[51];
    }
    t33 = tabs.map(t34);
    $[43] = active;
    $[44] = open;
    $[45] = openSource;
    $[46] = tabs;
    $[47] = t33;
  } else {
    t33 = $[47];
  }
  let t34;
  if ($[52] !== t32 || $[53] !== t33) {
    t34 = <div className={t20}>{t32}{t33}</div>;
    $[52] = t32;
    $[53] = t33;
    $[54] = t34;
  } else {
    t34 = $[54];
  }
  let t35;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="anatomy-status"><span><Icon name="graph" />main</span><span>Playground</span></div>;
    $[55] = t35;
  } else {
    t35 = $[55];
  }
  return <section id="explore" className="atlas-anatomy atlas-page-section" data-section={true} aria-label="Explore the Atlas project system">{t8}{t9}<div className="anatomy-viewer" data-reveal-surface={true} onKeyDown={t10}><nav className="anatomy-activity-rail" aria-label="Workspace views">{t12}<button className="anatomy-activity anatomy-settings" aria-label="Project settings" aria-pressed={active === "config"} onClick={() => open("config", true)}>{t13}{t14}</button></nav>{t19}{t34}{t35}</div></section>;
}